import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { config } from '../../../config';
import UpdateFlash from './UpdateFlash';
import CreateFlash from './CreateFlash';
import {
  FileText,
  Edit3,
  Trash2,
  RefreshCw,
  BookOpen
} from 'lucide-react';

const apiUrl = config.apiUrl;

const ViewFlash = ({ onBack }) => {
  const [flashCards, setFlashCards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFlashCard, setEditingFlashCard] = useState(null);
  const [formData, setFormData] = useState({
    subject_id: '',
    front: '',
    back: ''
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [flashResponse, subjectsResponse] = await Promise.all([
          axios.get(`${apiUrl}/flashes`),
          axios.get(`${apiUrl}/subjects`)
        ]);
        setFlashCards(flashResponse.data.data || flashResponse.data);
        setSubjects(subjectsResponse.data.data || subjectsResponse.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('ফ্ল্যাশকার্ড লোড করতে সমস্যা হয়েছে।');
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('আপনি কি এই ফ্ল্যাশকার্ডটি মুছতে চান?')) {
      try {
        await axios.delete(`${apiUrl}/flashes/${id}`);
        setFlashCards(prev => prev.filter(card => card.id !== id));
      } catch (err) {
        console.error('Error deleting flash card:', err);
        setError('ফ্ল্যাশকার্ড মুছতে সমস্যা হয়েছে।');
      }
    }
  };

  const handleEditClick = (flashCard) => {
    setEditingFlashCard(flashCard.id);
    setFormData({
      id: flashCard.id,
      subject_id: flashCard.subject_id,
      front: flashCard.front,
      back: flashCard.back
    });
  };

  const handleFlashCardCreated = async () => {
    try {
      const response = await axios.get(`${apiUrl}/flashes`);
      setFlashCards(response.data.data || response.data);
    } catch (err) {
      console.error('Error refreshing flash cards:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <button
                onClick={onBack}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <BookOpen className="h-5 w-5 text-gray-600" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-800 flex items-center">
                  <FileText className="h-6 w-6 mr-2 text-blue-600" />
                  ফ্ল্যাশকার্ড ম্যানেজমেন্ট
                </h1>
                <p className="text-gray-600 text-sm">ফ্ল্যাশকার্ড তৈরি এবং ব্যবস্থাপনা করুন</p>
              </div>
            </div>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-800 rounded-lg">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Create Flash Card Form */}
            <CreateFlash 
              onFlashCardCreated={handleFlashCardCreated} 
              subjects={subjects} 
            />

            {/* Flash Cards List */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold mb-6 text-center flex items-center justify-center">
                <FileText className="h-5 w-5 mr-2 text-blue-600" />
                সকল ফ্ল্যাশকার্ড
              </h3>
              
              {flashCards.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p>কোনো ফ্ল্যাশকার্ড পাওয়া যায়নি। প্রথম ফ্ল্যাশকার্ড তৈরি করুন!</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto">
                  {[...flashCards].reverse().map(card => (
                    <div key={card.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex justify-between items-start mb-3">
                        <h4 className="text-lg font-semibold text-gray-800">ফ্ল্যাশকার্ড</h4>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                          {card.subject?.name}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 gap-4 mb-3">
                        <div className="bg-gray-50 p-3 rounded">
                          <h5 className="font-medium text-gray-700 mb-2">সামনের অংশ:</h5>
                          <div
                            className="text-gray-600 whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{ __html: card.front }}
                          ></div>
                        </div>
                        <div className="bg-gray-50 p-3 rounded">
                          <h5 className="font-medium text-gray-700 mb-2">পিছনের অংশ:</h5>
                          <div
                            className="text-gray-600 whitespace-pre-wrap"
                            dangerouslySetInnerHTML={{ __html: card.back }}
                          ></div>
                        </div>
                      </div>

                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleEditClick(card)}
                          className="px-3 py-1 bg-yellow-500 text-white text-sm rounded-lg hover:bg-yellow-600 transition-colors flex items-center"
                        >
                          <Edit3 className="h-3 w-3 mr-1" />
                          সম্পাদনা
                        </button>
                        <button
                          onClick={() => handleDelete(card.id)}
                          className="px-3 py-1 bg-red-500 text-white text-sm rounded-lg hover:bg-red-600 transition-colors flex items-center"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          মুছুন
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {editingFlashCard && (
            <UpdateFlash
              formData={formData}
              setFormData={setFormData}
              subjects={subjects}
              onUpdate={(updatedCard) => {
                setFlashCards(prev =>
                  prev.map(card =>
                    card.id === editingFlashCard ? { ...card, ...updatedCard } : card
                  )
                );
                setEditingFlashCard(null);
              }}
              onCancel={() => setEditingFlashCard(null)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ViewFlash;